import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'react-feather'
import { Link, useLocation } from 'react-router-dom'
import styled, { ThemeContext } from 'styled-components'
import { darken } from 'polished'

import { getActiveChainId } from '../../config/active'
import { nativeCurrencyId } from '../../config/native'
import { CDONT } from '../../constants'
import { isInternalAllowlistedAccount } from '../../constants/internalAllowlist'
import { useAppWeb3 } from '../../hooks'
import { useDarkModeManager } from '../../state/user/hooks'
import { TYPE } from '../../theme'
import { RowBetween, RowFixed } from '../Row'
import Toggle from '../Toggle'
import QuestionHelper from '../QuestionHelper'

/**
 * Par de ejemplo para rutas embed add/remove con segmentos (nativo + C-DONT en minúsculas).
 * Debe coincidir con `currencyId()` usado en el resto de la app.
 */
const SAMPLE_NATIVE = nativeCurrencyId(getActiveChainId()).toLowerCase()
const SAMPLE_TOKEN_B = CDONT.address.toLowerCase()
const SAMPLE_NATIVE_LABEL = nativeCurrencyId(getActiveChainId())

const SANDBOX_ENTRIES: ReadonlyArray<{ to: string; label: string }> = [
  { to: '/embed/swap', label: 'Swap' },
  { to: '/embed/liquidity/positions', label: 'Liquidez · posiciones' },
  { to: '/embed/liquidity/import', label: 'Liquidez · importar pool' },
  { to: '/embed/liquidity/add', label: 'Liquidez · añadir (sin tokens)' },
  {
    to: `/embed/liquidity/add/${SAMPLE_NATIVE}/${SAMPLE_TOKEN_B}`,
    label: `Liquidez · añadir (${SAMPLE_NATIVE_LABEL} + C-DONT)`,
  },
  {
    to: `/embed/liquidity/remove/${SAMPLE_NATIVE}/${SAMPLE_TOKEN_B}`,
    label: `Liquidez · quitar (${SAMPLE_NATIVE_LABEL} + C-DONT)`,
  },
]

const APP_ENTRIES: ReadonlyArray<{ to: string; label: string }> = [
  { to: '/', label: 'Inicio' },
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/swap', label: 'Swap' },
  { to: '/pool', label: 'Pools' },
  { to: '/bridge', label: 'Bridge' },
  { to: '/diagnostics/rpc', label: 'Diagnóstico · RPC' },
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/profile', label: 'Profile' }
]

const Wrap = styled.div`
  position: relative;
  flex-shrink: 0;
`

const SandboxMenuButton = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0;
  padding: 0.4rem 0.65rem;
  border-radius: ${({ theme }) => theme.radii.control};
  border: 1px solid ${({ theme }) => theme.bg4};
  background: ${({ theme }) => theme.bg2};
  color: ${({ theme }) => theme.text1};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.primary1};
    color: ${({ theme }) => theme.primary1};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.focus.ring};
  }

`

const Chevron = styled(ChevronDown)<{ $open: boolean }>`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0')});
  transition: transform 0.15s ease;
`

/** Posición (`top`/`right`) desde JS vía portal: cualquier `overflow` del header ya no lo recorta. */
const Panel = styled.div`
  position: fixed;
  min-width: 280px;
  max-width: min(360px, 92vw);
  padding: 0.35rem 0;
  border-radius: ${({ theme }) => theme.radii.cardMd};
  background: ${({ theme }) => theme.bg1};
  border: 1px solid ${({ theme }) => theme.bg3};
  box-shadow: ${({ theme }) => theme.shadows.panel};
  z-index: 10090;
`

const PanelTitle = styled.div`
  padding: 0.5rem 0.85rem 0.35rem;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text3};
`

const ItemLink = styled(Link)`
  display: block;
  padding: 0.55rem 0.85rem;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.text1};
  text-decoration: none;

  &:hover {
    background: ${({ theme }) => darken(0.03, theme.bg2)};
    color: ${({ theme }) => theme.primary1};
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.focus.ring};
  }
`

const DiscloseTrigger = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin: 0;
  padding: 0.45rem 0.85rem;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text3};

  &:hover {
    color: ${({ theme }) => theme.text1};
    background: ${({ theme }) => darken(0.02, theme.bg2)};
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.focus.ring};
  }
`

const DiscloseChevron = styled(ChevronDown)<{ $open: boolean }>`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.text3};
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0')});
  transition: transform 0.15s ease;
`

const AppearanceBody = styled.div`
  padding: 0.35rem 0.85rem 0.65rem;
  border-top: 1px solid ${({ theme }) => theme.bg3};
  background: ${({ theme }) => theme.bg2};
`

const SelectCompact = styled.select`
  max-width: 9rem;
  font-size: 13px;
  padding: 0.2rem 0.35rem;
  border-radius: ${({ theme }) => theme.radii.control};
  border: 1px solid ${({ theme }) => theme.bg4};
  background: ${({ theme }) => theme.bg1};
  color: ${({ theme }) => theme.text1};
`

export default function EmbedSandboxMenu() {
  const { account } = useAppWeb3()
  const [open, setOpen] = useState(false)
  const [appearanceOpen, setAppearanceOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [panelPos, setPanelPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 })
  const location = useLocation()
  const themeCtx = useContext(ThemeContext)

  const [darkMode, toggleDarkMode] = useDarkModeManager()

  const close = useCallback(() => setOpen(false), [])

  const updatePanelPosition = useCallback(() => {
    const anchor = wrapRef.current
    if (!anchor || typeof window === 'undefined') return
    const r = anchor.getBoundingClientRect()
    setPanelPos({
      top: r.bottom + 6,
      right: Math.max(8, window.innerWidth - r.right)
    })
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    updatePanelPosition()
    window.addEventListener('resize', updatePanelPosition)
    window.addEventListener('scroll', updatePanelPosition, true)
    return () => {
      window.removeEventListener('resize', updatePanelPosition)
      window.removeEventListener('scroll', updatePanelPosition, true)
    }
  }, [open, updatePanelPosition])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (wrapRef.current?.contains(t) || panelRef.current?.contains(t)) return
      close()
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open, close])

  useEffect(() => {
    close()
    setAppearanceOpen(false)
  }, [location.pathname, close])

  if (!isInternalAllowlistedAccount(account)) {
    return null
  }

  return (
    <Wrap ref={wrapRef}>
      <SandboxMenuButton
        type="button"
        $open={open}
        aria-expanded={open}
        aria-haspopup="menu"
        id="embed-sandbox-menu-button"
        onClick={() => setOpen(o => !o)}
      >
        Sandbox
        <Chevron $open={open} aria-hidden />
      </SandboxMenuButton>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <Panel
              ref={panelRef}
              role="menu"
              aria-labelledby="embed-sandbox-menu-button"
              style={{ top: panelPos.top, right: panelPos.right }}
            >
              <PanelTitle>Cajón de arena (embed)</PanelTitle>
              {SANDBOX_ENTRIES.map(({ to, label }) => (
                <ItemLink key={to} to={to} role="menuitem" onClick={close}>
                  {label}
                </ItemLink>
              ))}
              <PanelTitle style={{ marginTop: '0.25rem' }}>App principal</PanelTitle>
              {APP_ENTRIES.map(({ to, label }) => (
                <ItemLink key={to} to={to} role="menuitem" onClick={close}>
                  {label}
                </ItemLink>
              ))}

              <DiscloseTrigger
                type="button"
                aria-expanded={appearanceOpen}
                onClick={() => setAppearanceOpen(o => !o)}
              >
                <span>Apariencia</span>
                <DiscloseChevron $open={appearanceOpen} aria-hidden />
              </DiscloseTrigger>
              {appearanceOpen ? (
                <AppearanceBody>
                  <RowBetween>
                    <RowFixed>
                      <TYPE.black fontWeight={400} fontSize={13} color={themeCtx?.text2}>
                        Modo oscuro
                      </TYPE.black>
                    </RowFixed>
                    <Toggle isActive={darkMode} toggle={toggleDarkMode} />
                  </RowBetween>
                </AppearanceBody>
              ) : null}
            </Panel>,
            document.body
          )
        : null}
    </Wrap>
  )
}
